import { useState } from 'react';
import { StrategyCaseStudyCard } from '@client/new/pages/onboarding/view/components/StrategyCaseStudyCard';
import {
  useInspirationStoriesModel,
  CaseStudy,
} from './InspirationStories.model';
import * as styles from './InspirationStories.css';

type InspirationStoriesProps = {
  creatorId: number;
};

type TabType = 'recommended' | 'other';

const mapToCaseStudyVM = (cs: CaseStudy) => ({
  id: cs.id,
  title: cs.title,
  subtitle: cs.subtitle,
  description: null,
  coverImageUrl: cs.coverImageUrl,
  creatorName: cs.creatorName,
  creatorInstagramName: cs.creatorInstagramName,
  documentUrl: cs.documentUrl,
});

export const InspirationStories = ({ creatorId }: InspirationStoriesProps) => {
  const [activeTab, setActiveTab] = useState<TabType>('recommended');
  const model = useInspirationStoriesModel(creatorId);

  if (model.type !== 'fetched') return null;

  const { recommendedCaseStudies, otherCaseStudies } = model.value;
  const hasRecommended = recommendedCaseStudies.length > 0;
  const hasOther = otherCaseStudies.length > 0;

  if (!hasRecommended && !hasOther) return null;

  const showTabs = hasRecommended && hasOther;
  const effectiveTab = hasRecommended ? activeTab : 'other';
  const caseStudies =
    effectiveTab === 'recommended' ? recommendedCaseStudies : otherCaseStudies;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Inspire with these stories</h2>
        <p className={styles.subtitle}>
          We've helped thousands of creators in creating their business. Look at
          the ones that started where you are!
        </p>
      </div>

      {showTabs && (
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${
              activeTab === 'recommended'
                ? styles.tabActive
                : styles.tabInactive
            }`}
            onClick={() => setActiveTab('recommended')}
          >
            Recommended for you
          </button>
          <button
            className={`${styles.tab} ${
              activeTab === 'other' ? styles.tabActive : styles.tabInactive
            }`}
            onClick={() => setActiveTab('other')}
          >
            Other stories
          </button>
        </div>
      )}

      <div className={styles.cardsContainer}>
        {caseStudies.slice(0, 4).map((cs) => (
          <div key={cs.id} className={styles.cardWrapper}>
            {cs.strategyName && (
              <span className={styles.strategyBadge}>{cs.strategyName}</span>
            )}
            <StrategyCaseStudyCard
              caseStudy={mapToCaseStudyVM(cs)}
              testId={`inspiration-story-${cs.id}`}
              titlePosition={cs.strategyName ? 'bottom' : 'top'}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
